import { FC, useState } from 'react';
import { TableBody as Body, Tooltip } from '@mui/material';
import { CheckOutlined, CloseOutlined } from '@mui/icons-material';
import dayjs from 'dayjs';

import { Badge, CustomAlert, TableNoData, Text } from '../../../../shared/uiComponents';
import { CustomTableRow, RowTableCell } from '../../../../shared/uiComponents/Table/tableHelpers';
import { HeaderProperties } from '../../../../shared/uiComponents/Table/tableProperties';
import { BadgeTypes, TargetStatusTypes } from '../../../../shared/uiComponents/StatusBadge';
import { Info } from '../../../../shared/Assets/svgIcons';
import { InfoRow } from '../../../../components/StyledComponents';

import { TargetProperties } from '../../../../redux/API/ClientAPIHelpers/programTargetsProperties';

interface RowRendererProperties {
	data: TargetProperties[];
}

export const MasteredTableHeaders: HeaderProperties[] = [
	{ id: '1', name: 'Target Name' },
	{ id: '2', name: 'Date Opened' },
	{ id: '3', name: 'Date Mastered' },
	{ id: '4', name: 'Status' },
	{ id: '5', name: 'Target Type' },
	{ id: '6', name: 'Action', width: '50px' },
];

const Action = ({ item }: { item: TargetProperties }) => {
	const [open, setOpen] = useState<boolean>(false);

	const onClose = () => setOpen(false);
	const onOpen = () => setOpen(true);

	return (
		<>
			<Tooltip title="More information">
				<div onClick={onOpen}>
					<Info />
				</div>
			</Tooltip>
			<CustomAlert
				title="Detailed Information"
				open={open}
				onClose={onClose}
				Content={() => {
					const {
						name,
						goalName,
						sd,
						type,
						errorCorrection,
						reinforcement,
						targetInstructions,
						dateMastered,
						responsePercentage,
						consecutiveDays,
						masteryCriteria,
						dateOpened,
						isReopened,
					} = item;

					return (
						<>
							<InfoRow>
								<Text title="Target Name" type="psm" fontWeight="semiBold" />
								<Text title={name} type="psm" />
							</InfoRow>
							<InfoRow>
								<Text title="Target Instructions" type="psm" fontWeight="semiBold" />
								<Text title={targetInstructions} type="psm" style={{ whiteSpace: 'pre-line' }} />
							</InfoRow>
							<InfoRow>
								<Text title="Goal Name" type="psm" fontWeight="semiBold" />
								<Text title={goalName} type="psm" />
							</InfoRow>
							<InfoRow>
								<Text title="SD" type="psm" fontWeight="semiBold" />
								<Text title={sd} type="psm" />
							</InfoRow>
							<InfoRow>
								<Text title="Target Type" type="psm" fontWeight="semiBold" />
								<Text title={type.name} type="psm" />
							</InfoRow>
							<InfoRow>
								<Text title="Response Percentage" type="psm" fontWeight="semiBold" />
								<Text title={responsePercentage ? responsePercentage : '-'} type="psm" />
							</InfoRow>
							<InfoRow>
								<Text title="Consecutive Days" type="psm" fontWeight="semiBold" />
								<Text title={consecutiveDays ? consecutiveDays : '-'} type="psm" />
							</InfoRow>
							<InfoRow>
								<Text title="Error Correction" type="psm" fontWeight="semiBold" />
								<Text title={errorCorrection} type="psm" />
							</InfoRow>
							<InfoRow>
								<Text title="Reinforcement" type="psm" fontWeight="semiBold" />
								<Text title={reinforcement} type="psm" />
							</InfoRow>
							<InfoRow>
								<Text title="Date Opened" type="psm" fontWeight="semiBold" />
								<Text title={dayjs(dateOpened).utc(false).format('MM/DD/YYYY')} type="psm" />
							</InfoRow>
							<InfoRow>
								<Text title="Reopened" type="psm" fontWeight="semiBold" />
								<div>{isReopened ? <CheckOutlined fontSize="small" /> : <CloseOutlined fontSize="small" />}</div>
							</InfoRow>
							<InfoRow>
								<Text title="Date Mastered" type="psm" fontWeight="semiBold" />
								<Text title={dayjs(dateMastered).utc(false).format('MM/DD/YYYY')} type="psm" />
							</InfoRow>
							<InfoRow>
								<Text title="Mastery Criteria" type="psm" fontWeight="semiBold" />
								<Text title={masteryCriteria.name} type="psm" />
							</InfoRow>
						</>
					);
				}}
			/>
		</>
	);
};

export const MasteredTableBody: FC<RowRendererProperties> = ({ data }) => {
	if (!data?.length) {
		return <TableNoData spanScope={MasteredTableHeaders.length} />;
	}

	return (
		<Body>
			{data.map((row) => (
				<CustomTableRow key={row.id} style={{ cursor: 'pointer' }}>
					<RowTableCell>{row.name}</RowTableCell>
					<RowTableCell>{dayjs(row.dateOpened).utc(false).format('MM/DD/YYYY')}</RowTableCell>
					<RowTableCell>{dayjs(row.dateMastered).utc(false).format('MM/DD/YYYY')}</RowTableCell>
					<RowTableCell>
						<Badge label={row.status.name} sizeType="sm" type={TargetStatusTypes[row.status.id] as BadgeTypes} />
					</RowTableCell>
					<RowTableCell>{row.type.name}</RowTableCell>
					<RowTableCell>
						<div style={{ float: 'right' }}>
							<Action item={row} />
						</div>
					</RowTableCell>
				</CustomTableRow>
			))}
		</Body>
	);
};
